<template>
  <v-card>
    <div
      ref="mapRef"
      class="map"
      style="height: 400px; width: 100%;"
    >
      <div
        v-if="!mapReady"
        id="loading-message"
        style="text-align: center; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"
      >
        Preparing map...<br>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { useMaps } from '~/composables/useMaps';

/**
 * View-only map field for showing multiple placemarks and centering the map on an
 * address. The caller can determine the zoom and centering, and we will re-draw if the
 * center position moves.
 *
 * @prop {LatLng} center - The center point for the map.
 * @prop {number} zoom - The zoom level of the map. Values 0-19, with each increment
 * halving the map size in each dimension.
 * @prop {AdvancedMarkerElement[]} placemarks - An array of placemarks. The caller
 * can style these. A simple map with have one placement on the center position.
 *
 * TODO: Change the zoom according to the range of the placemarks.
 */

const props = defineProps<{
  center: google.maps.LatLng
  placemarks: google.maps.marker.AdvancedMarkerElement[]
  zoom: number
}>();

const mapRef = ref<HTMLElement | null>(null);
const mapReady = ref(false);
const mapInstance = shallowRef<google.maps.Map | null>(null);

const { googleMapsEmbedApiKey, googleMapsMapId } = useRuntimeConfig().public;
const { getCoordinatesFromAddress } = await useMaps(googleMapsEmbedApiKey); // load once

const drawMap = async () => {
  if (!mapRef.value) {
    return;
  }

  const map = new google.maps.Map(mapRef.value, {
    zoom: props.zoom,
    center: props.center,
    mapId: googleMapsMapId,
    streetViewControl: false,
    zoomControlOptions: {
      position: google.maps.ControlPosition.LEFT_BOTTOM,
    },
  });

  // Assign map to placemarks
  if (props.placemarks?.length) {
    props.placemarks.forEach(marker => {
      marker.map = map;
    });
  };

  mapInstance.value = map;
  mapReady.value = true;
};

// adds and removes listener based on defined_by_coordinates
watch(
  () => props.center,
  async () => {
    await drawMap();
  },
);

onMounted(async () => {
  drawMap();
});
</script>

<style scoped>
.map {
  height: calc(50% + var(--v-layout-bottom) - 1rem);
  min-width: 480px;
}
</style>
